import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { apiGet, apiPost } from "./api";
import "./tournament.css";

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const pretty = (v) => String(v || "").replaceAll("_", " ");

export default function TournamentView() {
  const { id } = useParams();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  async function load() {
    try {
      const res = await apiGet(`admin/tournaments/${id}`);
      setData(res.data.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
  }
  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [id]);

  async function action(name, path) {
    if (!window.confirm(`Confirm ${name}?`)) return;
    try {
      setBusy(name);
      setError("");
      await apiPost(path);
      await load();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setBusy("");
    }
  }

  const summary = useMemo(() => {
    if (!data) return {};
    const t = data.tournament || {},
      matches = data.matches || [];
    return {
      completed: matches.filter((m) => ["COMPLETED", "BYE"].includes(m.status))
        .length,
      running: matches.filter((m) => m.status === "RUNNING").length,
      pending: matches.filter((m) => m.status === "PENDING").length,
      maxPrize: data.finance?.advertisedPrizePool || 0,
      currentPrize:
        data.finance?.currentPrizePool || data.finance?.prizePool || 0,
      collection: data.finance?.grossCollection || 0,
      reserve: data.finance?.platformGrossShare || 0,
      fill: Math.min(
        100,
        Math.round(
          (Number(t.humanJoined || 0) /
            Math.max(1, Number(t.maxPlayers || 1))) *
            100,
        ),
      ),
    };
  }, [data]);

  if (!data)
    return (
      <div className="tour-page">
        <div className="tour-card tour-empty">
          {error || "Loading tournament control center…"}
        </div>
      </div>
    );
  const t = data.tournament,
    standings = data.standings || [],
    matches = data.matches || [];
  const plan =
    (data.prizePlan && data.prizePlan.length
      ? data.prizePlan
      : data.advertisedPrizePlan) || [];
  const advertisedPlan = data.advertisedPrizePlan || [];

  return (
    <div className="tour-page">
      <div className="tour-head pro-head">
        <div>
          <div className="tour-eyebrow">LIVE TOURNAMENT CONTROL</div>
          <h1>{t.title}</h1>
          <div className="tour-sub">
            Finance, registrations, pairings, leaderboard, game engine and
            payouts.
          </div>
        </div>
        <div className="tour-actions">
          <button className="tour-btn soft" onClick={load}>
            ↻ Refresh
          </button>
          <button
            className="tour-btn soft"
            onClick={() => history.push("/admin/tournaments")}
          >
            All Tournaments
          </button>
        </div>
      </div>

      <section className="tour-control-hero">
        <div className="control-hero-main">
          <div className="control-title-row">
            <span
              className={`tour-badge ${t.status === "RUNNING" ? "live" : t.status === "COMPLETED" ? "gold" : ""}`}
            >
              {pretty(t.status)}
            </span>
            <span className="control-id">ID {String(t._id).slice(-8)}</span>
          </div>
          <h2>{t.title}</h2>
          <p>{t.subtitle}</p>
        </div>
        <div className="control-kpis">
          <div>
            <small>Entry Fee</small>
            <strong>{money(t.entryFee)}</strong>
          </div>
          <div className="accent">
            <small>Auto Prize Pool</small>
            <strong>{money(summary.maxPrize)}</strong>
          </div>
          <div>
            <small>Current Pool</small>
            <strong>{money(summary.currentPrize)}</strong>
          </div>
          <div>
            <small>Players</small>
            <strong>
              {t.humanJoined || 0}/{t.maxPlayers}
            </strong>
          </div>
          <div>
            <small>Round</small>
            <strong>
              {t.currentRound || 0}/{t.totalRounds}
            </strong>
          </div>
        </div>
      </section>

      <div className="tour-grid control-grid">
        <section className="tour-card span-8 pro-card">
          <div className="tour-section-head compact">
            <span className="tour-section-no">01</span>
            <div>
              <h3>Registration & Finance</h3>
              <p>Current confirmed entries and tournament economy.</p>
            </div>
          </div>
          <div className="tour-kpis pro-kpis">
            <div className="tour-kpi">
              <small>Current Collection</small>
              <strong>{money(summary.collection)}</strong>
            </div>
            <div className="tour-kpi">
              <small>Current Prize Pool</small>
              <strong className="gold-text">
                {money(summary.currentPrize)}
              </strong>
            </div>
            <div className="tour-kpi">
              <small>Current Reserve</small>
              <strong className="green-text">{money(summary.reserve)}</strong>
            </div>
            <div className="tour-kpi">
              <small>CPU Seats</small>
              <strong>{t.cpuJoined || 0}</strong>
            </div>
          </div>
          <div className="capacity-block">
            <div>
              <strong>Human Capacity</strong>
              <span>
                {t.humanJoined || 0} of {t.maxPlayers}
              </span>
            </div>
            <div className="tour-progress big">
              <span style={{ width: `${summary.fill}%` }} />
            </div>
            <small>
              {summary.fill}% filled • final finance locks when registration
              closes.
            </small>
          </div>
        </section>

        <section className="tour-card span-4 pro-card">
          <div className="tour-section-head compact">
            <span className="tour-section-no">02</span>
            <div>
              <h3>Admin Actions</h3>
              <p>Lifecycle controls.</p>
            </div>
          </div>
          <div className="tour-action-stack">
            {t.status === "DRAFT" && (
              <button
                disabled={busy}
                className="tour-btn gold full"
                onClick={() =>
                  action("publish", `admin/tournaments/${id}/publish`)
                }
              >
                Publish Tournament
              </button>
            )}
            {t.status === "REGISTRATION_OPEN" && (
              <button
                disabled={busy}
                className="tour-btn green full"
                onClick={() => action("start", `admin/tournaments/${id}/start`)}
              >
                Start Tournament Now
              </button>
            )}
            <button
              // disabled={Number(t.entryFee) > 0 || !t.allowCpuFill || busy}
              className="tour-btn soft full"
              title={
                Number(t.entryFee) > 0 ? "CPU fill free/practice mode only" : ""
              }
              onClick={() =>
                action("CPU fill", `admin/tournaments/${id}/fill-cpu`)
              }
            >
              Fill Free Seats With Expert CPU
            </button>
            {!["COMPLETED", "CANCELLED"].includes(t.status) && (
              <button
                disabled={busy}
                className="tour-btn red full"
                onClick={() =>
                  action("cancel", `admin/tournaments/${id}/cancel`)
                }
              >
                Cancel & Refund
              </button>
            )}
          </div>
          {Number(t.entryFee) > 0 && (
            <div className="tour-alert info small">
              Paid tournament me CPU fill disabled hai. Confirmed human entries
              ke basis par final prize list lock hogi.
            </div>
          )}
          {error && <div className="tour-alert error small">{error}</div>}
        </section>

        <section className="tour-card span-5 pro-card">
          <div className="tour-section-head compact">
            <span className="tour-section-no">03</span>
            <div>
              <h3>Current Prize List</h3>
              <p>
                {t.status === "REGISTRATION_OPEN"
                  ? "Live estimate based on confirmed entries."
                  : "Locked/settlement prize list."}
              </p>
            </div>
          </div>
          <div className="control-prizes">
            {plan.map((p, i) => (
              <div
                className={`control-prize ${i < 3 ? "top" : ""}`}
                key={`${p.fromRank}-${p.toRank}`}
              >
                <span>
                  <i>{i === 0 ? "♛" : i + 1}</i>
                  {p.fromRank === p.toRank
                    ? `Rank ${p.fromRank}`
                    : `Rank ${p.fromRank} - ${p.toRank}`}
                </span>
                <strong>
                  {money(p.amountPerRank)}
                  {p.fromRank !== p.toRank ? " each" : ""}
                </strong>
              </div>
            ))}
          </div>
          {t.status === "REGISTRATION_OPEN" && (
            <details className="tour-details">
              <summary>Full-capacity prize preview</summary>
              <div className="control-prizes mini">
                {advertisedPlan.map((p, i) => (
                  <div
                    className="control-prize"
                    key={`max-${p.fromRank}-${p.toRank}`}
                  >
                    <span>
                      {p.fromRank === p.toRank
                        ? `Rank ${p.fromRank}`
                        : `Rank ${p.fromRank} - ${p.toRank}`}
                    </span>
                    <strong>
                      {money(p.amountPerRank)}
                      {p.fromRank !== p.toRank ? " each" : ""}
                    </strong>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>

        <section className="tour-card span-7 pro-card">
          <div className="tour-section-head compact">
            <span className="tour-section-no">04</span>
            <div>
              <h3>Live Leaderboard</h3>
              <p>Points → Buchholz → wins → game tie-breaks.</p>
            </div>
          </div>
          <div className="tour-table-wrap">
            <table className="tour-table pro-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Player</th>
                  <th>Type</th>
                  <th>Pts</th>
                  <th>W</th>
                  <th>L</th>
                  <th>BH</th>
                </tr>
              </thead>
              <tbody>
                {standings.length ? (
                  standings.map((p, i) => (
                    <tr key={p._id}>
                      <td>
                        <span className="rank-chip">#{p.rank || i + 1}</span>
                      </td>
                      <td>
                        <strong>{p.displayName}</strong>
                      </td>
                      <td>
                        {p.isCpu ? (
                          <span className="tour-badge">CPU</span>
                        ) : (
                          "Human"
                        )}
                      </td>
                      <td>
                        <strong>{p.points}</strong>
                      </td>
                      <td>{p.wins}</td>
                      <td>{p.losses}</td>
                      <td>{p.buchholz || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="tour-empty">
                      No participants yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="tour-card span-12 pro-card">
          <div className="tour-section-head compact">
            <span className="tour-section-no">05</span>
            <div>
              <h3>Round Match Engine</h3>
              <p>
                Every pairing is connected to the existing secure Ludo game.
              </p>
            </div>
          </div>
          <div className="match-summary-grid">
            <div>
              <small>Total Matches</small>
              <strong>{matches.length}</strong>
            </div>
            <div>
              <small>Running</small>
              <strong className="green-text">{summary.running}</strong>
            </div>
            <div>
              <small>Pending</small>
              <strong>{summary.pending}</strong>
            </div>
            <div>
              <small>Completed</small>
              <strong>{summary.completed}</strong>
            </div>
          </div>
          <div className="tour-table-wrap">
            <table className="tour-table pro-table">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Table</th>
                  <th>Players</th>
                  <th>Status</th>
                  <th>Engine Game</th>
                  <th>Winner / Result</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {matches.length ? (
                  matches.map((m) => (
                    <tr key={m._id}>
                      <td>
                        <strong>R{m.round}</strong>
                      </td>
                      <td>#{m.tableNo}</td>
                      <td>
                        <strong>{m.playerAName}</strong>
                        <span className="vs">vs</span>
                        <strong>{m.playerBName}</strong>
                      </td>
                      <td>
                        <span
                          className={`tour-badge ${m.status === "RUNNING" ? "live" : m.status === "COMPLETED" ? "gold" : ""}`}
                        >
                          {m.status}
                        </span>
                      </td>
                      <td className="mono">{m.engineGameId || "—"}</td>
                      <td>{m.winnerName || m.resultReason || "—"}</td>
                      <td>
                        {m.durationMs
                          ? `${Math.round(m.durationMs / 1000)}s`
                          : "—"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="tour-empty">
                      Round pairings will appear here after tournament starts.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
